import React, { FC, useContext } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { InfoMessage, SuccessMessage, WarningMessage } from '@eika/attention-message';
import { CheckmarkList } from '@eika/checkmark-list';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import { SamtykkeStatusWeb } from '../../generated-types';
import useSamtykke from '../../hooks/useSamtykke';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter, { ByttonType } from '../page/pageFooter';

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Samtykke: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { samtykke } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: samtykkeUrl, isLoading, isFetched } = useSamtykke(soeknadstype);
    const kanSamtykke = samtykkeUrl?.status
        ? [SamtykkeStatusWeb.UNOPENED, SamtykkeStatusWeb.OPENED, SamtykkeStatusWeb.UNKNOWN].includes(samtykkeUrl.status)
        : false;

    const onClickSamtykke = () => {
        if (samtykkeUrl?.customerUrl && kanSamtykke) {
            window.location.replace(samtykkeUrl.customerUrl.toString());
        } else {
            navigate(navigateTo);
        }
    };

    const onHoverPersonvernUrl = (xPos: number, yPos: number, skalVise: boolean) => {
        const element = document.getElementById('personvernside-url');
        if (element) {
            element.style.display = skalVise ? 'block' : 'none';
            element.style.left = `${10 + window.scrollX + xPos}px`;
            element.style.top = `${15 + window.scrollY + yPos}px`;
        }
    };

    return (
        <PageCard title={samtykke.title}>
            <div>
                <div className="typography-ingress__light margin-bottom-medium-large">{samtykke.subTitle}</div>
                <div className="margin-bottom-larger">
                    <div className="margin-bottom-small">{`${samtykke.infoTitle}:`}</div>
                    <CheckmarkList
                        checkmarks={[
                            {
                                label: samtykke.infoSkatt,
                                checked: true,
                            },
                            {
                                label: samtykke.infoGjeld,
                                checked: true,
                            },
                            {
                                label: samtykke.inforDsf,
                                checked: true,
                            },
                            {
                                label: samtykke.infoKreditt,
                                checked: true,
                            },
                        ]}
                    />
                </div>
                {isLoading && <Loader />}
                {isFetched && (
                    <>
                        {kanSamtykke && (
                            <InfoMessage text={samtykke.infoSamtykke} icon={IconTypes.INFO_36} type="default" />
                        )}
                        {samtykkeUrl?.status === SamtykkeStatusWeb.ACCEPTED && (
                            <SuccessMessage
                                id="samtykkeAkesptert"
                                text={samtykke.infoSamtykkeAkseptert}
                                icon={IconTypes.CHECKMARK_TIL_SIRKEL_36}
                            />
                        )}
                        {samtykkeUrl?.customerUrl === null && (
                            <WarningMessage
                                id="samtykkeWarning"
                                text={samtykke.infoError}
                                icon={IconTypes.UTROPSTEGN_24}
                                className="margin-top-larger"
                            />
                        )}
                        <p className="margin-top-larger">
                            {samtykke.infoAutomatiskBehandlet}{' '}
                            <NavLink
                                to={`${window.location.origin}/personvern-cookies`}
                                target="_blank"
                                rel="noreferrer"
                                onMouseMove={(e) => onHoverPersonvernUrl(e.clientX, e.clientY, true)}
                                onMouseLeave={(e) => onHoverPersonvernUrl(e.clientX, e.clientY, false)}
                            >
                                bankens personvernside
                            </NavLink>
                            .
                        </p>
                        <div
                            id="personvernside-url"
                            className="personvernside-url"
                        >{`${window.location.origin}/personvern-cookies`}</div>

                        <PageFooter
                            soeknadId={samtykkeUrl?.soeknadId}
                            showSpinner={false}
                            buttonType={ByttonType.BUTTON}
                            onNesteClick={onClickSamtykke}
                            onForrigeClick={() => navigate(navigateFrom)}
                        />
                    </>
                )}
            </div>
        </PageCard>
    );
};

export default Samtykke;
